import { Injectable } from '@angular/core';

export interface SearchTextPart {
  text: string;
  matched: boolean;
}

@Injectable()
export class SearchService {
  private readonly noMatchScore = -1;

  constructor() {
  }

  searchText(toSearch: string, searcher: string): boolean {
    return this.getSearchScore(toSearch, searcher) > this.noMatchScore;
  }

  getSearchParts(text: string, searcher: string): SearchTextPart[] {
    text = text || '';
    if (!searcher || !searcher.trim() || this.getSearchScore(text, searcher) < 0) {
      return [{ text, matched: false }];
    }

    const ranges: { start: number; end: number }[] = [];
    const addRange = (start: number, end: number) => {
      ranges.push({ start, end });
    };
    const normalizedQuery = this.normalizeSearchValue(searcher);
    const compactQuery = this.compactSearchValue(normalizedQuery);
    let compactText = '';
    const positions: number[] = [];
    for (let index = 0; index < text.length; index++) {
      if (!/[-_\s]/.test(text[index])) {
        const character = text[index].toLowerCase();
        compactText += character;
        for (let offset = 0; offset < character.length; offset++) { positions.push(index); }
      }
    }

    // Map normalized matches back to the original text, including its separators.
    if (compactQuery) {
      let index = compactText.indexOf(compactQuery);
      while (index >= 0) {
        const start = positions[index];
        const end = positions[index + compactQuery.length - 1] + 1;
        // Short queries and phrases must begin at a word boundary, even if
        // another occurrence in this field already qualified it as a result.
        const validStart = (normalizedQuery.length > 2 && !normalizedQuery.includes(' '))
          || start === 0 || /[-_\s]/.test(text[start - 1]);
        if (validStart) {
          addRange(start, end);
        }
        index = compactText.indexOf(compactQuery, index + compactQuery.length);
      }
    }

    if (!ranges.length) {
      ranges.push(...this.getFuzzyMatchRanges(text, normalizedQuery));
    }

    const parts: SearchTextPart[] = [];
    let cursor = 0;
    ranges.sort((first, second) => first.start - second.start).forEach(range => {
      if (range.end <= cursor) { return; }
      if (range.start > cursor) { parts.push({ text: text.slice(cursor, range.start), matched: false }); }
      const start = Math.max(cursor, range.start);
      const previous = parts[parts.length - 1];
      if (previous && previous.matched && range.start <= cursor) {
        previous.text += text.slice(start, range.end);
      } else {
        parts.push({ text: text.slice(start, range.end), matched: true });
      }
      cursor = range.end;
    });
    if (cursor < text.length) { parts.push({ text: text.slice(cursor), matched: false }); }
    return parts.length ? parts : [{ text, matched: false }];
  }

  getSearchScore(toSearch: string, searcher: string): number {
    if (!toSearch) {
      return this.noMatchScore;
    }

    if (!searcher) {
      return 0;
    }

    const normalizedText = this.normalizeSearchValue(toSearch);
    const normalizedSearcher = this.normalizeSearchValue(searcher);
    const phrase = normalizedSearcher.includes(' ');

    if (normalizedText === normalizedSearcher) {
      return 1000;
    }

    if (normalizedText.startsWith(normalizedSearcher)) {
      return 900;
    }

    const wordBoundaryMatch = normalizedText.indexOf(` ${normalizedSearcher}`);
    const shortSearcher = normalizedSearcher.length <= 2;

    // Prefer matches that start at a word boundary before considering looser substring matches.
    if (wordBoundaryMatch >= 0) {
      return 800;
    }

    // Keep very short searches strict so common letter pairs do not match unrelated words.
    if (shortSearcher) {
      return this.noMatchScore;
    }

    // Match after normalizing punctuation and whitespace differences.
    if (!normalizedSearcher.includes(' ') && normalizedText.includes(normalizedSearcher)) {
      return 700;
    }

    const compactText = this.compactSearchValue(normalizedText);
    const compactSearcher = this.compactSearchValue(normalizedSearcher);

    if (compactText === compactSearcher) {
      return 650;
    }

    if (!phrase && compactText.startsWith(compactSearcher)) {
      return 600;
    }

    // Do not allow compact substring matching for very short queries.
    if (shortSearcher) {
      return this.noMatchScore;
    }

    // Match when separators are removed entirely, e.g. "jackup" vs "jack-up".
    const compactPhrasePattern = phrase
      ? normalizedText.split(' ').some((_, index, words) => words.slice(index).join('').startsWith(compactSearcher))
      : compactText.includes(compactSearcher);
    if (compactPhrasePattern) {
      return 500;
    }

    // Fall back to token-level fuzzy matching for small typos and partial words.
    if (this.matchesFuzzyTokens(normalizedText, normalizedSearcher)) {
      return 300;
    }

    return this.noMatchScore;
  }

  private normalizeSearchValue(value: string): string {
    return value
      .toLowerCase()
      .replace(/[-_\s]+/g, ' ')
      .trim();
  }

  private compactSearchValue(value: string): string {
    return value.replace(/\s+/g, '');
  }

  private matchesFuzzyTokens(text: string, searcher: string): boolean {
    return this.getFuzzyMatchRanges(text, searcher).length > 0;
  }

  private getFuzzyMatchRanges(text: string, searcher: string): { start: number; end: number }[] {
    const words: { text: string; start: number }[] = [];
    const pattern = /[^-_\s]+/g;
    let word: RegExpExecArray;
    while ((word = pattern.exec(text)) !== null) {
      words.push({ text: word[0].toLowerCase(), start: word.index });
    }
    const tokens = searcher.split(' ').filter(token => !!token);
    const ranges: { start: number; end: number }[] = [];
    if (!tokens.length) { return ranges; }

    // A fuzzy phrase must match consecutive words in query order. Scoring
    // and highlighting use these same ranges so they explain the same match.
    for (let index = 0; index <= words.length - tokens.length; index++) {
      if (!tokens.every((token, offset) => {
        const textToken = words[index + offset].text;
        // The final token is still being typed; allow even one-letter prefixes.
        return (offset === tokens.length - 1 && textToken.startsWith(token))
          || this.matchesFuzzyToken(textToken, token);
      })) {
        continue;
      }
      tokens.forEach((token, offset) => {
        const matchedWord = words[index + offset];
        ranges.push({
          start: matchedWord.start,
          end: matchedWord.start + (matchedWord.text.startsWith(token) ? token.length : matchedWord.text.length)
        });
      });
    }
    return ranges;
  }

  private matchesFuzzyToken(textToken: string, searchToken: string): boolean {
    if (searchToken.length <= 2) {
      return textToken === searchToken;
    }
    // Allow direct partial matches before applying stricter fuzzy rules.
    if (textToken.startsWith(searchToken)) {
      return true;
    }

    const minLength = Math.min(textToken.length, searchToken.length);

    // Separator fragments such as "de" in "de-rig" must not match words like "blade".
    if (minLength <= 2) {
      return false;
    }

    if (minLength <= 4) {
      // Keep short-word fuzzy matching tight so unrelated terms do not leak through.
      if (textToken[0] !== searchToken[0]) {
        return false;
      }

      return this.getLevenshteinDistance(textToken, searchToken) <= 1;
    }

    if (minLength < 6) {
      return false;
    }

    if (Math.abs(textToken.length - searchToken.length) > 2) {
      return false;
    }

    if (textToken.substring(0, 2) !== searchToken.substring(0, 2)) {
      return false;
    }

    // Allow more edits for longer words where common misspellings are more likely.
    const allowedDistance = minLength >= 7 ? 2 : 1;

    return this.getLevenshteinDistance(textToken, searchToken) <= allowedDistance;
  }

  private getLevenshteinDistance(first: string, second: string): number {
    const previousRow = new Array(second.length + 1);
    const currentRow = new Array(second.length + 1);

    for (let column = 0; column <= second.length; column++) {
      previousRow[column] = column;
    }

    for (let row = 1; row <= first.length; row++) {
      currentRow[0] = row;

      for (let column = 1; column <= second.length; column++) {
        const substitutionCost = first[row - 1] === second[column - 1] ? 0 : 1;

        currentRow[column] = Math.min(
          currentRow[column - 1] + 1,
          previousRow[column] + 1,
          previousRow[column - 1] + substitutionCost
        );
      }

      for (let column = 0; column <= second.length; column++) {
        previousRow[column] = currentRow[column];
      }
    }

    return previousRow[second.length];
  }
}



// WEBPACK FOOTER //
// ./src/services/search/search.service.ts